import { PermissionCode, type TicketListSortBy } from '@gb-ticket/shared-types';
import { useVoiceCopy } from '@/platform/voice/use-voice';
import { ChevronLeftIcon, ChevronRightIcon, Loader2Icon } from 'lucide-react';
import { Link } from 'react-router';

import { Button } from '@/components/ui/button';
import { hasPermission } from '@/features/auth/permissions';
import { useMe } from '@/features/auth/use-me';
import { useAppPaths } from '@/products/helpdesk/features/portal/portal-paths';
import { TicketsFilters } from '@/products/helpdesk/features/tickets/tickets-filters';
import { TicketsTable } from '@/products/helpdesk/features/tickets/tickets-table';
import { usePersistedTicketListQuery } from '@/products/helpdesk/features/tickets/use-persisted-ticket-list-query';
import { useTicketLookups, useTickets } from '@/products/helpdesk/features/tickets/use-tickets';
import { ApiRequestError } from '@/lib/api';
import { usePageTitle } from '@/hooks/use-page-title';

export function PortalTicketsPage() {
  const copy = useVoiceCopy();
  usePageTitle('Meine Anfragen');
  const me = useMe();
  const paths = useAppPaths();
  const canCreate = hasPermission(me.data, PermissionCode.TICKETS_CREATE);
  const lookups = useTicketLookups();
  const { query, setQuery, patchQuery, searchInput, setSearchInput, ready } =
    usePersistedTicketListQuery('portal', me.data?.id);
  const tickets = useTickets(query, { enabled: ready });

  const totalPages = tickets.data
    ? Math.max(1, Math.ceil(tickets.data.total / tickets.data.pageSize))
    : 1;

  function onSortChange(sortBy: TicketListSortBy) {
    setQuery((current) => ({
      ...current,
      sortBy,
      sortOrder: current.sortBy === sortBy && current.sortOrder === 'asc' ? 'desc' : 'asc',
      page: 1,
    }));
  }

  return (
    <section className="min-w-0 space-y-4">
      <div className="flex min-w-0 flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
        <div className="min-w-0">
          <h1 className="text-2xl font-bold tracking-tight">Meine Anfragen</h1>
          <p className="mt-1 text-sm text-muted-foreground">
            {copy('helpdesk.portal.tickets.only-yours')}
          </p>
        </div>
        {canCreate && (
          <Button
            asChild
            className="bg-brand-red text-white hover:bg-brand-red/90 focus-visible:ring-brand-red/30"
          >
            <Link to={paths.ticketNew}>Neue Anfrage</Link>
          </Button>
        )}
      </div>

      <TicketsFilters
        variant="simple"
        query={query}
        lookups={lookups.data}
        searchInput={searchInput}
        onSearchInputChange={setSearchInput}
        onChange={patchQuery}
      />

      {(!ready || tickets.isPending) && (
        <p className="flex items-center gap-2 text-muted-foreground">
          <Loader2Icon className="size-4 animate-spin" aria-hidden />
          {copy('helpdesk.portal.tickets.loading')}
        </p>
      )}
      {tickets.isError && (
        <p role="alert" className="text-sm text-destructive">
          {tickets.error instanceof ApiRequestError
            ? tickets.error.message
            : 'Die Liste konnte nicht geladen werden.'}
        </p>
      )}
      {tickets.data && (
        <>
          <div className="min-w-0 overflow-x-auto rounded-md border bg-card">
            <TicketsTable items={tickets.data.items} query={query} onSortChange={onSortChange} />
          </div>
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <p className="text-sm text-muted-foreground" aria-live="polite">
              {tickets.data.total === 0
                ? copy('helpdesk.portal.tickets.empty')
                : `${tickets.data.total} Anfragen · Seite ${tickets.data.page} von ${totalPages}`}
            </p>
            <div className="flex gap-2">
              <Button
                variant="outline"
                size="sm"
                disabled={query.page <= 1}
                onClick={() => patchQuery({ page: query.page - 1 })}
              >
                <ChevronLeftIcon />
                Zurück
              </Button>
              <Button
                variant="outline"
                size="sm"
                disabled={query.page >= totalPages}
                onClick={() => patchQuery({ page: query.page + 1 })}
              >
                Weiter
                <ChevronRightIcon />
              </Button>
            </div>
          </div>
        </>
      )}
    </section>
  );
}
